// import components
import { ControlledTimePeriods, Dialog } from 'components/shared';
import { Button, Stack, TextField } from '@mui/material';
import DutyLineList from './UncontrolledDutyLineList';

// import external libraries
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { queries } from 'RestAPIs/rapini';

// import hooks
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// import schema
import { dutyPeriodDialogSchema } from './DutyPeriodDialog.schema';

// import styles
import { StyledDutyLineAutocomplete } from './DutyLineOptionEditCell.styles';

// import types
import {
  DutyCodeOption,
  DutyPeriodDialogProps,
  PeriodWithId
} from './DutyPeriodDialog.types';

// import utils
import { isEqual } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';

const DutyPeriodDialog = ({
  isOpen,
  onClose,
  row,
  onSave
}: DutyPeriodDialogProps) => {
  const { t } = useTranslation();
  const { data: dutyCodes = [] } = true
    ? queries.useGetDutyCodes(
        dayjs(row?.dateKey).toISOString(),
        undefined,
        false,
        { enabled: !!row?.dateKey }
      )
    : { data: [] };
  const baseDate = dayjs(row?.dateKey).startOf('day');

  const options: DutyCodeOption[] = dutyCodes.map((duty) => ({
    value: duty.dutyCodeId,
    label: duty.dutyCode?.startsWith('§')
      ? duty.code ?? ''
      : String(duty.dutyCode ?? '')
  }));

  const current =
    options.find((option) =>
      typeof row?.displayDutyCode === 'number'
        ? option.value === row?.displayDutyCode
        : option.label === row?.displayDutyCode
    ) ?? null;

  const defaultValues = useMemo(() => {
    const periods = (
      row?.periods ?? [
        {
          dutyHourTypeId: null,
          startTime: null,
          endTime: null
        }
      ]
    ).map((period) => ({
      dutyCodePeriodId: period.dutyCodePeriodId ?? null,
      dutyHourTypeId:
        period.dutyHourType?.dutyHourTypeId ?? period.dutyHourType?.id,
      dutyHourType: {
        code: period.dutyHourType?.code,
        description: period.dutyHourType?.description,
        dutyHourTypeId:
          period.dutyHourType?.dutyHourTypeId ?? period.dutyHourType?.id
      },
      startTime: dayjs(period.startTime),
      endTime: dayjs(period.endTime)
    }));

    return {
      dutyCode: current,
      periods
    };
  }, [current, row?.periods]);

  const methods = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(dutyPeriodDialogSchema)
  });

  const { control, handleSubmit, watch, setValue } = methods;

  const dutyCodeOption = watch('dutyCode');
  const dutyCodePeriods = watch('periods');

  const selectedDutyCodePeriods =
    dutyCodes
      ?.find((duty) => duty.dutyCodeId === dutyCodeOption?.value)
      ?.periods?.map(
        ({ dutyHourType, dutyCodePeriodId: _dutyCodePeriodId, ...period }) => ({
          ...period,
          dutyHourType: {
            code: dutyHourType?.code ?? '',
            description: dutyHourType?.description ?? '',
            dutyHourTypeId: dutyHourType?.dutyHourTypeId ?? null
          },
          startTime: dayjs(period.startTime).format('HH:mm').toString(),
          endTime: dayjs(period.endTime).format('HH:mm').toString()
        })
      ) ?? [];

  const watchedPeriods = (dutyCodePeriods as PeriodWithId[])?.map(
    ({
      id: _id, //  the id is added by the RH Form, we don't need it
      dutyCodePeriodId: _dutyCodePeriodId,
      dutyHourTypeId,
      ...period
    }) => ({
      ...period,
      dutyHourType: {
        code: period.dutyHourType?.code ?? '',
        description: period.dutyHourType?.description ?? '',
        dutyHourTypeId
      },
      startTime: dayjs(period.startTime).format('HH:mm').toString(),
      endTime: dayjs(period.endTime).format('HH:mm').toString()
    })
  );

  const isCustomizedFields = !isEqual(selectedDutyCodePeriods, watchedPeriods);

  const handleDutyCodeChange = (_, option) => {
    setValue('dutyCode', option, { shouldValidate: true });
    const dutyCode = dutyCodes.find((d) => d.dutyCodeId === option?.value);

    const mapped = (dutyCode?.periods ?? []).map((period) => ({
      dutyCodePeriodId: period?.dutyCodePeriodId ?? null,
      dutyHourTypeId: period?.dutyHourType?.dutyHourTypeId ?? null,
      startTime: dayjs(period.startTime),
      endTime: dayjs(period.endTime),
      dutyHourType: {
        dutyHourTypeId: period.dutyHourType?.dutyHourTypeId ?? null,
        code: period.dutyHourType?.code ?? '',
        description: period.dutyHourType?.description ?? ''
      }
    }));

    setValue('periods', mapped, { shouldDirty: false, shouldValidate: true });
  };

  const onSubmit = () => {
    const periods = dutyCodePeriods.map((period) => ({
      dutyCodePeriodId: period?.dutyCodePeriodId ?? null,
      dutyHourTypeId: period?.dutyHourTypeId,
      startTime: period.startTime.toISOString(),
      endTime: period.endTime.toISOString(),
      dutyHourType: {
        code: period.dutyHourType?.code,
        description: period.dutyHourType?.description,
        dutyHourTypeId: period?.dutyHourTypeId
      }
    }));

    onSave?.(dutyCodeOption, periods);
    onClose?.();
  };

  useEffect(() => {
    if (isCustomizedFields)
      setValue('dutyCode', null, {
        shouldDirty: true,
        shouldValidate: true
      });
  }, [isCustomizedFields]);

  if (!row) return null;

  return (
    <Dialog
      open={isOpen}
      title={t('periods')}
      fullWidth
      handleClose={onClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: '400px',
            maxWidth: '600px',
            maxHeight: 'calc(100% - 200px)'
          }
        }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={6}>
            <StyledDutyLineAutocomplete
              options={options}
              value={dutyCodeOption}
              onChange={(_, option) => handleDutyCodeChange(_, option)}
              blurOnSelect
              autoHighlight={false}
              getOptionKey={(option) =>
                String(option.value ?? crypto.randomUUID())
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <DutyLineList
                    key={key}
                    option={option}
                    dutyCodes={dutyCodes}
                    {...rest}
                  />
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  slotProps={{
                    ...params.InputProps,
                    input: {
                      ...params.InputProps,
                      ref: params.InputProps.ref,
                      startAdornment: <>{params.InputProps.endAdornment}</>
                    }
                  }}
                />
              )}
              fullWidth
              slotProps={{
                popper: {
                  sx: {
                    width: 'max-content',
                    '.MuiAutocomplete-paper': { width: '100%' }
                  }
                }
              }}
            />

            <ControlledTimePeriods
              control={control}
              watch={watch}
              baseDate={baseDate}
              hasDHTSelector
              hideBreakDuration
              fixTimeToBaseDate
            />

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button onClick={onClose}>{'Cancel'}</Button>
              <Button type="submit" variant="contained">
                {'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default DutyPeriodDialog;
